import { AxiosInstance } from 'axios';

/**
 * Infrastructure for dynamic field value fetching
 * Currently returns placeholders - future: integrate with backend APIs
 */

/**
 * Async function type for field value fetching
 */
export type FieldValueFetcher = () => Promise<string[]>;

/**
 * Fetch Asset tags from statistics endpoint
 * Requires authenticated axios instance with proper token and account headers
 */
export async function fetchAssetTags(
  axios: AxiosInstance,
  options?: { isGlobal?: boolean }
): Promise<string[]> {
  const response = await axios.get<StatisticsResponse>('/my', {
    params: {
      label: 'statistic',
      key: '#statistic#asset#tags#now#',
      ...(options?.isGlobal ? { allTenants: true } : {}),
    },
  });

  return response.data.statistics.map((item: { value: string }) => item.value);
}

/**
 * Fetch Asset origins from statistics endpoint
 * Requires authenticated axios instance with proper token and account headers
 */
export async function fetchAssetOrigins(
  axios: AxiosInstance,
  options?: { isGlobal?: boolean }
): Promise<string[]> {
  const response = await axios.get<StatisticsResponse>('/my', {
    params: {
      label: 'statistic',
      key: '#statistic#asset#origin#now#',
      ...(options?.isGlobal ? { allTenants: true } : {}),
    },
  });

  return response.data.statistics.map((item: { value: string }) => item.value);
}

/**
 * Response structure from statistics endpoint
 */
interface StatisticsResponse {
  count: number;
  statistics: Array<{ value: string }>;
}

/**
 * Fetch Active Directory domains from statistics endpoint
 * Requires authenticated axios instance with proper token and account headers
 * @param axios - Authenticated AxiosInstance from useAxios hook
 * @returns Promise resolving to array of domain names
 */
export async function fetchADObjectDomains(
  axios: AxiosInstance,
  options?: { isGlobal?: boolean }
): Promise<string[]> {
  const response = await axios.get<StatisticsResponse>('/my', {
    params: {
      label: 'statistic',
      key: '#statistic#adobject#domains#now#',
      ...(options?.isGlobal ? { allTenants: true } : {}),
    },
  });

  // API returns { count, statistics: [{ value }] } structure
  // Map statistics array to string array: [{ value: "domain1.com" }] => ["domain1.com"]
  return response.data.statistics.map((item: { value: string }) => item.value);
}

/**
 * Helper to create a field value fetcher
 * Wraps async function with error handling
 */
export function createValueFetcher(
  fetchFn: () => Promise<string[]>
): FieldValueFetcher {
  return async () => {
    try {
      return await fetchFn();
    } catch (error) {
      console.error('Failed to fetch field values:', error);
      return [];
    }
  };
}
